/* eslint-disable no-nested-ternary */
/* eslint-disable react/require-default-props */

import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Model, { Providers } from "@tripian/model";
import { api, easy, providers } from "@tripian/core";
import useBookableTours from "../../../../../../hooks/useBookableTours";
import {
  StepInfo,
  PoiInfo,
  AccommondationInfo,
  Modal,
  Booking,
  Button,
  PreLoading,
  CloseIconButton,
  ReservationDetails,
  BbAccommodationInfo,
  BbCarRentInfo,
  RezdyTourInfo,
  ConfirmModalPopup,
} from "@tripian/react";
import {
  PLACE_INFO,
  QR_READER,
} from "../../../../../../constants/ROUTER_PATH_TITLE";
import useTrip from "../../../../../../hooks/useTrip";
import usePlan from "../../../../../../hooks/usePlan";
import useStep from "../../../../../../hooks/useStep";
import useFavorite from "../../../../../../hooks/useFavorite";
import useReservation from "../../../../../../hooks/useReservation";
import useFocus from "../../../../../../hooks/useFocus";
import useUser from "../../../../../../hooks/useUser";
import useTranslate from "../../../../../../hooks/useTranslate";
import { useDispatch } from "react-redux";
import {
  changeLoadingReference,
  saveNotification,
} from "../../../../../../redux/action/trip";
import useFeedBack from "../../../../../../hooks/useFeedback";
import { CloneModal } from "../../../../../../components/CloneModal/CloneModal";
import classes from "./FocusContainer.module.scss";

interface IFocusContainer {
  isBookingClicked: boolean;
  planId?: number;
  isLoadingOffer: (offerId: number) => boolean;
  myAllOffers?: Model.Poi[];
  offerOptIn: (offerId: number, optInDate: string) => Promise<void>;
  offerOptOut: (offerId: number) => Promise<void>;
  gygTourIds: number[];
  bbTourIds: number[];
  viatorTourIds: string[];
  toristyTourIds: string[];
  toursLoading: boolean;
  hideActionButtons?: boolean;
  hideFavoriteIcon?: boolean;
  hidePartOfDay?: boolean;
  hideFeatures?: boolean;
  hideCuisine?: boolean;
  timezone?: string;
  onImageModalChange?: (isOpen: boolean) => void;
  onTourClick: (
    provider: Model.PROVIDER_ID,
    tourId: string,
    additionalData?: any
  ) => void;
}

const FocusContainer: React.FC<IFocusContainer> = ({
  isBookingClicked,
  planId,
  isLoadingOffer,
  myAllOffers,
  offerOptIn,
  offerOptOut,
  gygTourIds,
  bbTourIds,
  viatorTourIds,
  toristyTourIds,
  toursLoading,
  hideActionButtons = false,
  hideFavoriteIcon = false,
  hidePartOfDay = false,
  hideFeatures = false,
  hideCuisine = false,
  timezone,
  onImageModalChange,
  onTourClick,
}) => {
  const [showCloneModal, setShowCloneModal] = useState<boolean>(false);
  const [bookingModalState, setBookingModalState] = useState<{
    show: boolean;
    businessId: string;
    isEditMode: boolean;
    poi?: Model.Poi;
  }>({
    show: false,
    businessId: "",
    isEditMode: false,
  });
  const [deleteModalState, setDeleteModalState] = useState<{
    show: boolean;
    stepId: number;
  }>({ show: false, stepId: -1 });

  const [availabilityCheckEnabled, setAvailabilityCheckEnabled] = useState(false);

  const { tripReference, tripUpdate, tripFetchCallback, tripReadOnly } =
    useTrip();
  const { plans } = usePlan();
  const { stepAdd, stepDelete, stepReplace } = useStep();
  const { favorites, favoriteAdd, favoriteDelete, loadingFavoritePoi } =
    useFavorite();
  const {
    reservationAdd,
    loadingReservation,
    yelpReservations,
    reservationCancel,
  } = useReservation();
  const { focus, focusLost } = useFocus();
  const { user } = useUser();
  const { loadingFeedback, feedbacks, feedbackSend } = useFeedBack();

  // Get current focused POI
  const focusedPoi = useMemo(() => {
    if (focus.step) return focus.step.poi;
    if (focus.alternative) return focus.alternative.poi;
    if (focus.poi) return focus.poi;
    return null;
  }, [focus.step, focus.alternative, focus.poi]);

  const { t } = useTranslate();

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const planDate: string | undefined = useMemo(
    () => plans?.find(currentPlan => currentPlan.id === planId)?.date,
    [planId, plans]
  );

  const travelDate = useMemo(() => {
    const focusedStepId = focus.step?.id;
    const planContainingStep = focusedStepId
      ? plans?.find(p => p.steps.some(s => s.id === focusedStepId))
      : undefined;
    const selectedPlan = planContainingStep || plans?.find(p => p.id === planId);
    return selectedPlan?.date
      ? moment(selectedPlan.date).format("YYYY-MM-DD")
      : tripReference?.tripProfile.arrivalDatetime
        ? moment(tripReference.tripProfile.arrivalDatetime).format("YYYY-MM-DD")
        : moment().add(1, "days").format("YYYY-MM-DD");
  }, [focus.step, plans, planId, tripReference]);

  // Bookable ürünler POST /tour-api/search üzerinden POI modunda gelir; instantAvailability
  // müsaitliği aynı istekte döndürdüğü için ayrı bir schedule turu yoktur. Fetch, kullanıcı
  // "Bookable Experiences" sekmesine geçene kadar (availabilityCheckEnabled) beklemede kalır.
  const {
    products: bookableProducts,
    loading: bookableLoading,
    loadingMore: bookableLoadingMore,
    hasMore: bookableHasMore,
    fetchProducts: fetchBookableProducts,
    loadMore: loadMoreBookableProducts,
    reset: resetBookableProducts,
  } = useBookableTours({
    poiId: focusedPoi?.id,
    cityId: tripReference?.city.id,
    date: travelDate,
    adults: tripReference?.tripProfile.numberOfAdults || 1,
    currency: "usd",
    lang: window.twindow?.langCode || "en",
    limit: 10,
  });

  const { hash } = useParams<{ hash: string }>();

  const focusContainerClasses = [classes.focusContainer];
  if (!window.tconfig.SHOW_OVERVIEW)
    focusContainerClasses.push("container-height");
  else focusContainerClasses.push("container-height-tab");
  if (
    focus.step ||
    focus.alternative ||
    focus.poi ||
    focus.providerPoi ||
    focus.carRentOffer ||
    focus.providerTour
  )
    focusContainerClasses.push(classes.focusContainerOpen);
  else focusContainerClasses.push(classes.focusContainerClose);

  const isFavorite = (poiId: string) =>
    favorites
      ? favorites.findIndex(favorite => favorite.poiId === poiId) > -1
      : false;
  const favoriteToggle = (add: boolean, poiId: string) => {
    if (add) {
      favoriteAdd(poiId);
    } else {
      const favoriteId: number =
        favorites?.find(favorite => favorite.poiId === poiId)?.id || 0;
      if (favoriteId) favoriteDelete(favoriteId, poiId);
      // eslint-disable-next-line no-console
      else
        console.error(
          "favoriteToggle favorite delete error: Can not found poi id in favorite list",
          add,
          poiId,
          favorites
        );
    }
  };

  const getPartOfDay = useCallback(() => {
    const findDaysNumbers = (poiId: string): number[] | undefined => {
      if (poiId === "") return undefined;

      if (plans) {
        const partOfDayIndexes: number[] = [];
        for (let i = 0; i < plans.length; i += 1) {
          const stepIndex = plans[i].steps.findIndex(
            step => step.poi.id === poiId
          );
          if (stepIndex > -1) {
            // console.log('getPartOfDay dayNumber', stepIndex + 1);
            partOfDayIndexes.push(i + 1);
            // return i + 1;
          }
        }
        return partOfDayIndexes;
      }
      return undefined;
    };

    if (focus.step) {
      return findDaysNumbers(focus.step.poi.id);
    }

    if (focus.alternative) {
      return findDaysNumbers(focus.alternative.poi.id);
    }

    if (focus.poi) {
      return findDaysNumbers(focus.poi.id);
    }

    return undefined;
  }, [focus.alternative, focus.poi, focus.step, plans]);

  const sendFeedback = (feedback: Model.FeedbackRequest, poiId?: string) => {
    const newFeedback = { ...feedback };
    newFeedback.poiId = poiId;
    newFeedback.tripHash = tripReference?.tripHash;
    newFeedback.desc = feedback.desc.replace(/^\s+|\s+$/g, "");
    return feedbackSend(newFeedback);
  };

  const memoizedReservationClicked = useCallback(
    (productId: string, poi: Model.Poi) => {
      if (!poi.bookings) return;
      const providerId =
        window.tconfig.RESTAURANT_RESERVATION_PROVIDER_IDS.find(pId =>
          poi.bookings.some(booking => booking.providerId === pId)
        );
      const booking = poi.bookings.find(c => c.providerId === providerId);

      if (booking) {
        const productUrl = booking.products[0].url;

        if (booking.providerId === Model.PROVIDER_ID.OPEN_TABLE) {
          window.open(productUrl || "https://www.opentable.com");
        }
        if (booking.providerId === Model.PROVIDER_ID.YELP) {
          window.open(productUrl || "https://www.yelp.com");
        }
        // else {
        //   if (window.tconfig.PROVIDERS.restaurant.find((x) => x.id === Model.PROVIDER_ID.YELP)?.prod ?? true) {
        //     window.open(productUrl || "https://www.yelp.com");
        //   } else {
        //     setBookingModalState({ show: true, businessId: productId, isEditMode: false, poi });
        //   }
        // }
      }
    },
    []
  );

  const memoizedGetTourInfo = useCallback(
    (productId: string) => {
      const product = bookableProducts.find(p => p.productId === productId);
      if (!product) return;

      onTourClick(product.providerId as Model.PROVIDER_ID, productId, {
        cityId: tripReference?.city.id,
        cityName: tripReference?.city.name,
        startDate: tripReference?.tripProfile.arrivalDatetime,
        endDate: tripReference?.tripProfile.departureDatetime,
        adults: tripReference?.tripProfile.numberOfAdults,
        children: tripReference?.tripProfile.numberOfChildren,
        url: product.url,
      });
    },
    [onTourClick, tripReference, bookableProducts]
  );

  const memoizedReservationRequest = useCallback(
    (reservationModel: Providers.Yelp.ReservationRequest) =>
      providers.yelp?.reservation(
        reservationModel
      ) as Promise<Providers.Yelp.Reservation>,
    []
  );

  const memoizedBookingCallback = useCallback(
    (
      provider: string,
      poi: Model.Poi,
      reservationDetails: Providers.Yelp.ReservationRequest,
      response?: Providers.Yelp.Reservation
    ) => {
      if (response) {
        const reservationInfo: Providers.Yelp.ReservationInfo = {
          confirm_url: response.confirmation_url,
          reservation_id: response.reservation_id || "",
          restaurant_image: (poi.image && poi.image.url) ?? "",
          restaurant_name: poi.name,
          reservation_details: reservationDetails,
        };

        const startDateTime = `${reservationDetails.date} ${reservationDetails.time}`;
        const formattedStartDateTime = moment(startDateTime).format(
          "YYYY-MM-DD HH:mm:ss"
        );

        reservationAdd(
          {
            key: Model.PROVIDER_NAME.YELP,
            tripHash: hash,
            poiId: poi.id,
            provider,
            value: reservationInfo,
            bookingDateTime: formattedStartDateTime,
          },
          false
        ).finally(() => {
          setBookingModalState({
            show: false,
            businessId: "",
            isEditMode: false,
          });
        });
      }

      return null;
    },
    [hash, reservationAdd]
  );

  const memoizedCurrentReservationUrl = useCallback(
    (poiId: string) => {
      const currentUserReservation = yelpReservations?.find(
        reservation => reservation.poiId === poiId
      )?.value as Providers.Yelp.ReservationInfo;

      if (currentUserReservation) {
        return currentUserReservation.confirm_url;
      }

      return undefined;
    },
    [yelpReservations]
  );

  const offerButtonOnChange = (
    optIn: boolean,
    offer: Model.Offer,
    optInDate: string
  ) => {
    if (tripReadOnly) {
      cloneTrip();
    } else {
      if (optIn) {
        const baseDate = moment(optInDate);
        const currentTime = moment();
        const dateTime = baseDate
          .set({
            hour: currentTime.hour(),
            minute: currentTime.minute(),
            second: currentTime.second(),
          })
          .tz(tripReference?.city.timezone || "UTC")
          .format("YYYY-MM-DD HH:mm:ss");

        if (offer.offerType === Model.OFFER_TYPE.VOUCHER) {
          const tdate = new easy.TDate({
            startDate: offer.timeframe.start,
            endDate: offer.timeframe.end,
            recurrent: offer.timeframe.recurrent,
            blackouts: offer.timeframe.blackouts,
          });
          if (tdate.toList().includes(optInDate)) {
            offerOptIn(offer.id, dateTime);
          } else {
            dispatch(
              saveNotification(
                Model.NOTIFICATION_TYPE.WARNING,
                t("notification.offerOptIn.name"),
                t("notification.offerOptIn.message")
              )
            );
          }
        } else {
          offerOptIn(offer.id, dateTime);
        }
      } else {
        return offerOptOut(offer.id);
      }
    }
  };

  // YelpReservation Api Requests for inner form
  const yelpGetBusinesInfo = useCallback(
    (businessId: string) =>
      providers.yelp?.business(businessId) as Promise<Providers.Yelp.Business>,
    []
  );

  const yelpOpeningRequest = useCallback(
    (openings: Providers.Yelp.OpeningsRequest) =>
      providers.yelp?.openings(openings) as Promise<Providers.Yelp.Openings>,
    []
  );

  const yelpGetHoldIt = useCallback(
    (hold: Providers.Yelp.HoldRequest) =>
      providers.yelp?.hold(hold) as Promise<Providers.Yelp.Hold>,
    []
  );

  // Reset the bookable list when focused POI changes, so the next POI does not
  // inherit the previous one's products or its pagination offset.
  const prevFocusedPoiRef = useRef<string | null>(null);
  useEffect(() => {
    const currentPoiId = focusedPoi?.id ?? null;
    if (currentPoiId !== prevFocusedPoiRef.current) {
      prevFocusedPoiRef.current = currentPoiId;
      resetBookableProducts();
      setAvailabilityCheckEnabled(false);
    }
  }, [focusedPoi, resetBookableProducts]);

  // Stable identity: PoiInfoText notifies through an effect, so an inline arrow here
  // would re-run that effect on every render of the focus panel.
  const enableBookableFetch = useCallback(() => setAvailabilityCheckEnabled(true), []);

  // Fetch the first page for the focused POI when user opens the Bookable Experiences tab
  useEffect(() => {
    if (
      !availabilityCheckEnabled ||
      !focusedPoi?.hasBookings ||
      !window.tconfig.SHOW_TOURS_AND_TICKETS
    ) return;

    fetchBookableProducts();
    // focusedPoi objesine değil id'sine bağlı: focus objesi ilgisiz redux
    // güncellemelerinde yeniden üretilebiliyor ve bu effect'i boşuna tetikliyordu.
  }, [
    availabilityCheckEnabled,
    focusedPoi?.id,
    focusedPoi?.hasBookings,
    fetchBookableProducts,
  ]);

  const memoizedBookingModal = useMemo(() => {
    const currentReservation = yelpReservations?.find(
      reservation => reservation.poiId === bookingModalState.poi?.id
    );
    const coversCount =
      (tripReference?.tripProfile.numberOfAdults || 0) +
      (tripReference?.tripProfile.numberOfChildren || 0);
    const userProfileBooking: {
      firstName: string;
      lastName: string;
      email: string;
      coversCount: number;
    } = {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      coversCount,
    };

    return (
      <Modal
        show={bookingModalState.show}
        className={`${classes.bookingModal} p5`}
        backdropClick={() => {
          if (!loadingReservation)
            setBookingModalState({
              show: false,
              businessId: "",
              isEditMode: false,
            });
        }}
      >
        <div>
          <div className={`${classes.bookingModalCloseIcon} m1`}>
            <CloseIconButton
              fill="#fff"
              clicked={() => {
                if (!loadingReservation)
                  setBookingModalState({
                    show: false,
                    businessId: "",
                    isEditMode: false,
                  });
              }}
            />
          </div>
          {loadingReservation ? (
            <div className={classes.focusContainerLoading}>
              <PreLoading />
            </div>
          ) : null}
          <div>
            {currentReservation && !bookingModalState.isEditMode ? (
              <ReservationDetails
                reservationInfo={
                  currentReservation.value as Providers.Yelp.ReservationInfo
                }
                t={t}
              />
            ) : bookingModalState.poi && bookingModalState.show ? (
              <Booking
                businessId={bookingModalState.businessId}
                poi={bookingModalState.poi}
                stepDate={planDate || ""}
                // stepHour={focus.step?.hours.from}
                userProfileBooking={userProfileBooking}
                getBusinessInfo={yelpGetBusinesInfo}
                openingsRequest={yelpOpeningRequest}
                getHoldIt={yelpGetHoldIt}
                reservationRequest={memoizedReservationRequest}
                bookingCallback={memoizedBookingCallback}
                t={t}
              />
            ) : null}
          </div>
          {currentReservation && !bookingModalState.isEditMode ? (
            <div className="row center">
              <div>
                <Button
                  color="primary"
                  text="Cancel Reservation"
                  onClick={() => {
                    setBookingModalState(prevState => ({
                      ...prevState,
                    }));
                    reservationCancel(
                      (
                        currentReservation?.value as Providers.Yelp.ReservationInfo
                      ).reservation_id
                    )?.finally(() => {
                      setBookingModalState({
                        show: false,
                        businessId: "",
                        isEditMode: false,
                      });
                    });
                  }}
                />
              </div>
            </div>
          ) : null}
        </div>
      </Modal>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    yelpReservations,
    tripReference?.tripProfile.numberOfAdults,
    tripReference?.tripProfile.numberOfChildren,
    user?.firstName,
    user?.lastName,
    user?.email,
    bookingModalState.show,
    bookingModalState.isEditMode,
    bookingModalState.poi,
    bookingModalState.businessId,
    loadingReservation,
    planDate,
    yelpGetBusinesInfo,
    yelpOpeningRequest,
    yelpGetHoldIt,
    memoizedReservationRequest,
    memoizedBookingCallback,
    reservationCancel,
  ]);

  const cloneTrip = () => {
    setShowCloneModal(true);
  };

  const providerNameMap: Record<number, string> = {
    [Model.PROVIDER_ID.BOOK_BARBADOS]: "Bookbarbados",
    [Model.PROVIDER_ID.VIATOR]: "Viator",
    [Model.PROVIDER_ID.GYG]: "GetYourGuide",
    [Model.PROVIDER_ID.GOCITY]: "GoCity",
    [Model.PROVIDER_ID.TORISTY]: "Toristy",
    [Model.PROVIDER_ID.CIVITATIS]: "Civitatis",
  };

  // Backend's /tour-api/search has no explicit ticket/tour type field (unlike the old
  // poi.bookings[].products[].info array, which carried Model.BOOKING_PRODUCT_INFO.TICKET).
  // Best-effort until backend adds a real field: treat an exact "ticket" tag as a ticket,
  // everything else as a tour. This is not reliable — GYG tagging is inconsistent (some
  // skip-the-line entry products are also tagged "tours"), and partial matches like
  // "entry tickets" are deliberately NOT treated as a ticket signal since guided tours
  // carry that tag too.
  const isTicketProduct = (product: Model.TourSearchProduct): boolean =>
    product.tags?.some(tag => tag.toLowerCase() === "ticket") ?? false;

  // No client-side availability filtering: the search request already carries
  // instantAvailability=1, so what the backend returns is what we render.
  const availableBookableProducts = useMemo(
    () =>
      bookableProducts.map(product => ({
        ...product,
        provider: providerNameMap[product.providerId] || "",
      })),
    [bookableProducts]
  );

  const tourProducts = useMemo(
    () => availableBookableProducts.filter(product => !isTicketProduct(product)),
    [availableBookableProducts]
  );

  const ticketProducts = useMemo(
    () => availableBookableProducts.filter(isTicketProduct),
    [availableBookableProducts]
  );

  const renderContent = () => {
    if (focus.step) {
      if (focus.step.id === 0) {
        /*
          id: 0,
          icon: "Homebase",
          name: accommendation.name || "AccommendationState Name",
          description: accommendation.address || "AccommendationState Address",
          coordinate: accommendation.coordinate || { lat: 0, lng: 0 },
          web: accommendation.refID ?? "",
          address: accommendation.provider ?? "Google",
          image: {
            url: accommendation.imageUrl ?? "",
            imageOwner: { title: "", url: ""  },
            width: null,
            height: null,
          },
         */
        const accommodation: Model.Accommodation = {
          refID: focus.step.poi.web ?? "FocusContainer_renderContent_imposible",
          name: focus.step.poi.name,
          address: focus.step.poi.description,
          coordinate: focus.step.poi.coordinate,
          imageUrl: (focus.step.poi.image && focus.step.poi.image.url) ?? "",
          provider: focus.step.poi.address,
        };
        return (
          <AccommondationInfo
            accommodation={accommodation}
            close={() => {
              focusLost();
            }}
            t={t}
          />
        );
      }

      return (
        <div className="scrollable-y">
          <StepInfo
            key={`focus_poi_${focus.step.id}`}
            dayNumbers={getPartOfDay() || [500]}
            step={focus.step}
            favorite={isFavorite(focus.step.poi.id)}
            toggleFavorite={(poi: Model.Poi, willFavorite: boolean) => {
              if (tripReadOnly) {
                cloneTrip();
              } else {
                // console.log(`${poi.name} added to favorites..`);
                favoriteToggle(willFavorite, poi.id);
              }
            }}
            close={() => {
              focusLost();
            }}
            removeStep={stepId => {
              if (tripReadOnly) {
                cloneTrip();
              } else {
                setDeleteModalState({ show: true, stepId });
              }
            }}
            reservationButtonClick={memoizedReservationClicked}
            planDate={planDate}
            // planDate={planDate && focus.step.hour.from ? `${planDate} ${focus.step.hours.from}` : undefined}
            favoriteLoading={loadingFavoritePoi(focus.step.poi.id)}
            reservationUrl={memoizedCurrentReservationUrl(focus.step.poi.id)}
            hideBookingButton={!window.tconfig.SHOW_RESTAURANT_RESERVATIONS}
            hideTours={!window.tconfig.SHOW_TOURS_AND_TICKETS}
            onBookableTabClick={enableBookableFetch}
            getTourInfo={memoizedGetTourInfo}
            tourProducts={tourProducts}
            ticketProducts={ticketProducts}
            tourTicketProductsLoading={toursLoading || bookableLoading}
            bookableProductsLoadingMore={bookableLoadingMore}
            bookableProductsHasMore={bookableHasMore}
            onLoadMoreBookableProducts={loadMoreBookableProducts}
            // TOUR_PROVIDER_IDS={window.tconfig.TOUR_TICKET_PROVIDER_IDS}
            // TICKET_PROVIDER_IDS={window.tconfig.TOUR_TICKET_PROVIDER_IDS}
            RESTAURANT_RESERVATION_PROVIDER_IDS={
              window.tconfig.RESTAURANT_RESERVATION_PROVIDER_IDS
            }
            myOffers={myAllOffers || []}
            isLoadingOffer={isLoadingOffer}
            offerButtonClick={offerButtonOnChange}
            hideOffers={!window.tconfig.SHOW_OFFERS}
            loadingFeedback={loadingFeedback}
            feedbackSubjects={feedbacks?.poiSubjects || []}
            sendFeedback={(feedback: Model.FeedbackRequest) => {
              if (tripReadOnly) {
                cloneTrip();
                return Promise.resolve();
              } else {
                return sendFeedback(feedback, focus.step?.poi.id);
              }
            }}
            placeInfoCallBack={() =>
              window.open(`${PLACE_INFO.PATH}/${focus.step?.poi.id}`, "_blank")
            }
            showStepScoreDetails={window.tconfig.SHOW_STEP_SCORE_DETAIL}
            hideActionButtons={hideActionButtons}
            hideFavoriteIcon={hideFavoriteIcon}
            hidePartOfDay={hidePartOfDay}
            hideFeatures={hideFeatures}
            hideCuisine={hideCuisine}
            isBookingClicked={isBookingClicked}
            timezone={timezone}
            onImageModalChange={onImageModalChange}
            t={t}
          />
        </div>
      );
    }

    if (focus.alternative)
      return (
        <div className="scrollable-y">
          <PoiInfo
            dayNumbers={getPartOfDay()}
            key={`focus_alternative_poi_${focus.alternative.poi.id}`}
            poi={focus.alternative.poi}
            favorite={isFavorite(focus.alternative.poi.id)}
            toggleFavorite={(poi: Model.Poi, willFavorite: boolean) => {
              if (tripReadOnly) {
                cloneTrip();
              } else {
                // console.log(`${poi.name} added to favorites..`);
                favoriteToggle(willFavorite, poi.id);
              }
            }}
            close={() => {
              focusLost();
            }}
            addRemoveReplacePoi={(poi: Model.Poi, flag: number) => {
              if (tripReadOnly) {
                cloneTrip();
              } else {
                focusLost();
                if (flag === 1) {
                  // add poi to step
                  console.error(
                    "FocusContainer: step add called by alternative poi!"
                  ); // eslint-disable-line no-console
                } else if (flag === 0) {
                  // replace poi to step
                  if (focus.alternative) {
                    focusLost();
                    stepReplace(
                      focus.alternative.stepId,
                      focus.alternative.poi.id
                    );
                  } else
                    console.error(
                      "FocusContainer: step replace called without replaceStepId!"
                    ); // eslint-disable-line no-console
                } else if (flag === -1) {
                  // remove poi from step
                  // eslint-disable-next-line no-console
                  console.error(
                    `FocusContainer:
                  PoiInfo never calls this remove step prop directly.
                  Only StepInfo component handle this event with StepInfo.removeStep`
                  );
                }
              }
            }}
            replace
            favoriteLoading={loadingFavoritePoi(focus.alternative.poi.id)}
            reservationButtonClick={memoizedReservationClicked}
            reservationUrl={memoizedCurrentReservationUrl(
              focus.alternative.poi.id
            )}
            hideBookingButton={!window.tconfig.SHOW_RESTAURANT_RESERVATIONS}
            square={false}
            hideTours={!window.tconfig.SHOW_TOURS_AND_TICKETS}
            planDate={planDate}
            myOffers={myAllOffers || []}
            isLoadingOffer={isLoadingOffer}
            offerButtonClick={offerButtonOnChange}
            hideOffers={!window.tconfig.SHOW_OFFERS}
            tourProducts={tourProducts}
            ticketProducts={ticketProducts}
            tourTicketProductsLoading={toursLoading || bookableLoading}
            bookableProductsLoadingMore={bookableLoadingMore}
            bookableProductsHasMore={bookableHasMore}
            onLoadMoreBookableProducts={loadMoreBookableProducts}
            // TOUR_PROVIDER_IDS={window.tconfig.TOUR_TICKET_PROVIDER_IDS}
            // TICKET_PROVIDER_IDS={window.tconfig.TOUR_TICKET_PROVIDER_IDS}
            RESTAURANT_RESERVATION_PROVIDER_IDS={
              window.tconfig.RESTAURANT_RESERVATION_PROVIDER_IDS
            }
            getTourInfo={(productId: string) => {}}
            loadingFeedback={loadingFeedback}
            feedbackSubjects={feedbacks?.poiSubjects || []}
            sendFeedback={(feedback: Model.FeedbackRequest) => {
              if (tripReadOnly) {
                cloneTrip();
                return Promise.resolve();
              } else {
                return sendFeedback(feedback, focus.alternative?.poi.id);
              }
            }}
            placeInfoCallBack={() =>
              window.open(
                `${PLACE_INFO.PATH}/${focus.alternative?.poi.id}`,
                "_blank"
              )
            }
            redeemClicked={() => navigate(QR_READER.PATH)}
            hideActionButtons={hideActionButtons}
            hideFavoriteIcon={hideFavoriteIcon}
            isBookingClicked={isBookingClicked}
            onBookableTabClick={enableBookableFetch}
            timezone={timezone}
            t={t}
          />
        </div>
      );

    if (focus.poi)
      return (
        <div className="scrollable-y">
          <PoiInfo
            key={`focus_search_poi_${focus.poi.id}`}
            dayNumbers={getPartOfDay()}
            poi={focus.poi}
            favorite={isFavorite(focus.poi.id)}
            toggleFavorite={(poi: Model.Poi, willFavorite: boolean) => {
              if (tripReadOnly) {
                cloneTrip();
              } else {
                // console.log(`${poi.name} added to favorites..`);
                favoriteToggle(willFavorite, poi.id);
              }
            }}
            close={() => {
              focusLost();
            }}
            addRemoveReplacePoi={(
              poi: Model.Poi,
              flag: number,
              from?: string,
              to?: string
            ) => {
              if (tripReadOnly) {
                cloneTrip();
              } else {
                focusLost();

                if (flag === 1) {
                  // add poi to step
                  if (planId) stepAdd(planId, poi.id, from, to);
                  else
                    console.error(
                      "FocusContainer: step add called without planId!"
                    ); // eslint-disable-line no-console
                } else if (flag === 0) {
                  // replace poi to step
                  // if (focus.poi) dispatch(stepReplace(focus.alternative.stepId, focus.alternative.poi.id));
                  // else console.error('FocusContainer: step replace called without replaceStepId!'); // eslint-disable-line no-console
                  console.error(
                    "FocusContainer: step replace called with same poi!"
                  ); // eslint-disable-line no-console
                } else if (flag === -1) {
                  // remove poi from step
                  // eslint-disable-next-line no-console
                  console.error(
                    `FocusContainer:
                  PoiInfo never calls this remove step prop directly.
                  Only StepInfo component handle this event with StepInfo.removeStep`
                  );
                }
              }
            }}
            replace={false}
            favoriteLoading={loadingFavoritePoi(focus.poi.id)}
            reservationButtonClick={memoizedReservationClicked}
            reservationUrl={memoizedCurrentReservationUrl(focus.poi.id)}
            hideBookingButton={!window.tconfig.SHOW_RESTAURANT_RESERVATIONS}
            square={false}
            hideTours={!window.tconfig.SHOW_TOURS_AND_TICKETS}
            planDate={planDate}
            myOffers={myAllOffers || []}
            isLoadingOffer={isLoadingOffer}
            offerButtonClick={offerButtonOnChange}
            hideOffers={!window.tconfig.SHOW_OFFERS}
            tourProducts={tourProducts}
            ticketProducts={ticketProducts}
            tourTicketProductsLoading={toursLoading || bookableLoading}
            bookableProductsLoadingMore={bookableLoadingMore}
            bookableProductsHasMore={bookableHasMore}
            onLoadMoreBookableProducts={loadMoreBookableProducts}
            // TOUR_PROVIDER_IDS={window.tconfig.TOUR_TICKET_PROVIDER_IDS}
            // TICKET_PROVIDER_IDS={window.tconfig.TOUR_TICKET_PROVIDER_IDS}
            RESTAURANT_RESERVATION_PROVIDER_IDS={
              window.tconfig.RESTAURANT_RESERVATION_PROVIDER_IDS
            }
            getTourInfo={memoizedGetTourInfo}
            loadingFeedback={loadingFeedback}
            feedbackSubjects={feedbacks?.poiSubjects || []}
            sendFeedback={(feedback: Model.FeedbackRequest) => {
              if (tripReadOnly) {
                cloneTrip();
                return Promise.resolve();
              } else {
                return sendFeedback(feedback, focus.poi?.id);
              }
            }}
            placeInfoCallBack={() =>
              window.open(`${PLACE_INFO.PATH}/${focus.poi?.id}`, "_blank")
            }
            redeemClicked={() => navigate(QR_READER.PATH)}
            hideActionButtons={hideActionButtons}
            hideFavoriteIcon={hideFavoriteIcon}
            isBookingClicked={isBookingClicked}
            onBookableTabClick={enableBookableFetch}
            timezone={timezone}
            t={t}
            plans={plans || []}
            currentDayIndex={plans?.findIndex(p => p.id === planId) ?? 0}
            onTimelineAdd={(selectedPlanId, poiId, from, to) => {
              focusLost();
              stepAdd(selectedPlanId, poiId, from, to);
            }}
          />
        </div>
      );

    if (focus.providerPoi && tripReference) {
      const checkIn = moment(tripReference.tripProfile.arrivalDatetime).format(
        "MMM DD,YYYY"
      );
      const checkOutDefault = moment(
        tripReference.tripProfile.departureDatetime
      ).format("MMM DD,YYYY");

      const checkOut =
        checkIn === checkOutDefault
          ? moment(tripReference.tripProfile.departureDatetime)
              .add("day", 1)
              .format("MMM DD,YYYY")
          : checkOutDefault;

      return (
        <div className="scrollable-y">
          <BbAccommodationInfo
            adults={tripReference?.tripProfile.numberOfAdults || 1}
            children={
              tripReference?.tripProfile.numberOfChildren
                ? tripReference?.tripProfile.numberOfChildren
                : 0
            }
            checkIn={checkIn}
            checkOut={checkOut}
            key={`focus_search_provider_poi_${focus.providerPoi.info.hotelCode}`}
            hotelOffer={focus.providerPoi}
            onBookNow={url => {
              console.log(focus.providerPoi, url);
              window.open(url);
            }}
            onAddToItinerary={(accommodation: Model.Accommodation) => {
              console.log("Update Trip accommodation with ", accommodation);
              if (tripReference) {
                dispatch(changeLoadingReference(true));
                tripUpdate(tripReference.tripHash, {
                  ...tripReference.tripProfile,
                  accommodation,
                  doNotGenerate: 0,
                }).then((trip: Model.Trip) => {
                  tripFetchCallback(trip);
                  dispatch(changeLoadingReference(false));
                });
              }
            }}
            close={() => {
              focusLost();
            }}
            t={t}
            /* 

            close={() => {
              focusLost();
            }} 

            bookingButtonClick={memoizedBookingClicked}
            
            */
          />
        </div>
      );
    }

    if (focus.carRentOffer && tripReference) {
      const pickUpDateTime = moment(
        tripReference.tripProfile.arrivalDatetime
      ).format("MMM DD,YYYY");
      const dropOffDateTimeDefault = moment(
        tripReference.tripProfile.departureDatetime
      ).format("MMM DD,YYYY");

      const dropOffDateTime =
        pickUpDateTime === dropOffDateTimeDefault
          ? moment(tripReference.tripProfile.departureDatetime)
              .add("day", 1)
              .format("MMM DD,YYYY")
          : dropOffDateTimeDefault;

      return (
        <div className="scrollable-y">
          <BbCarRentInfo
            pickUpDateTime={pickUpDateTime}
            dropOffDateTime={dropOffDateTime}
            key={`focus_search_car_rent_info_${focus.carRentOffer.locationCode}`}
            offer={focus.carRentOffer}
            onRentNow={url => {
              // console.log(focus.carRentOffer, url);
              window.open(url);
            }}
            close={() => {
              focusLost();
            }}
            dateOfBirth={user?.dateOfBirth}
            t={t}
          />
        </div>
      );
    }

    if (focus.providerTour && tripReference) {
      return (
        <div className="scrollable-y">
          <RezdyTourInfo
            key={`focus_tour_info_${focus.providerTour.latitude}_${focus.providerTour.longitude}`}
            product={focus.providerTour}
            onBookingNow={() => {
              if (focus.providerTour?.name) {
                const bookingWidgetUrl = `https://365adventures.rezdy.com/calendarWidget/${focus.providerTour.productCode}`;
                window.open(bookingWidgetUrl);
              }
            }}
            close={() => {
              focusLost();
            }}
            t={t}
          />
        </div>
      );
    }

    return null;
  };

  const modalFinally = useCallback(() => {
    setDeleteModalState({ show: false, stepId: -1 });
  }, []);

  // useEffect(() => {
  //   if (isBookingClicked && focus.step?.poi) {
  //     const productId = focus.step.poi.id;
  //     memoizedReservationClicked(productId, focus.step.poi);
  //   } else if (isBookingClicked && focus.poi) {
  //     const productId = focus.poi.id;
  //     memoizedReservationClicked(productId, focus.poi);
  //   } else if (isBookingClicked && focus.alternative?.poi) {
  //     const productId = focus.alternative.poi.id;
  //     memoizedReservationClicked(productId, focus.alternative.poi);
  //   }
  // }, [isBookingClicked, focus.step, focus.poi, focus.alternative, memoizedReservationClicked]);

  return (
    <>
      {memoizedBookingModal}
      <div className={focusContainerClasses.join(" ")}>{renderContent()}</div>
      {showCloneModal && (
        <CloneModal
          sharedTripHash={tripReference?.tripHash ?? ""}
          onCancel={() => setShowCloneModal(false)}
        />
      )}
      <ConfirmModalPopup
        openModalPopup={deleteModalState.show}
        text={t("plan.removeStep.question")}
        confirmButtonText={t("plan.removeStep.submit")}
        cancelButtonText={t("plan.removeStep.cancel")}
        action={async () => {
          focusLost();
          await stepDelete(deleteModalState.stepId);
        }}
        confirmationCallback={modalFinally}
        cancelCallback={modalFinally}
        className={classes.comfirmModal}
      />
    </>
  );
};

export default FocusContainer;
